import clsx from 'clsx';
import { ReactNode, useCallback, useMemo } from 'react';
import { Field } from 'components/uikit/Field';
import {
  PillButtonGroup,
  PillSelectionMode,
} from 'components/uikit/buttons/pill/PillButtonGroup';
import { DropDownInput } from 'components/uikit/inputs/dropdown/DropDownInput';
import { MultiSelectDropDownInput } from 'components/uikit/inputs/dropdown/MultiSelectDropDownInput';
import {
  getMultipleValues,
  getSingleValue,
  pruneSelection,
  resolveChain,
} from './cascade';
import {
  CascadeControl,
  CascadeLevel,
  CascadeOption,
  CascadeSelection,
} from './types';
import dropdownStyles from 'components/uikit/DropdownSelector.module.scss';
import styles from './CascadingFilter.module.scss';

export type CascadingFilterProps<TValue> = {
  root: CascadeLevel<TValue>;
  value: CascadeSelection<TValue>;

  /** Receives the selection with the values of no longer reachable levels removed. */
  onChange: (value: CascadeSelection<TValue>) => void;

  disabled?: boolean;
  className?: string;
  levelClassName?: string;

  testId?: string;
};

export function CascadingFilter<TValue>(props: CascadingFilterProps<TValue>) {
  const { root, value, onChange, disabled, className, levelClassName, testId } =
    props;

  const chain = useMemo(() => resolveChain(root, value), [root, value]);

  const onLevelChange = useCallback(
    (
      level: CascadeLevel<TValue>,
      levelValue: TValue | TValue[] | undefined,
    ) => {
      onChange(pruneSelection(root, { ...value, [level.key]: levelValue }));
    },
    [root, value, onChange],
  );

  return (
    <div data-test-id={testId} className={clsx(styles.filter, className)}>
      {chain.map((level) => (
        <CascadeLevelView
          key={level.key}
          level={level}
          value={value}
          disabled={disabled}
          className={levelClassName}
          onChange={onLevelChange}
        />
      ))}
    </div>
  );
}

type CascadeLevelViewProps<TValue> = {
  level: CascadeLevel<TValue>;
  value: CascadeSelection<TValue>;
  disabled?: boolean;
  className?: string;
  onChange: (
    level: CascadeLevel<TValue>,
    value: TValue | TValue[] | undefined,
  ) => void;
};

function CascadeLevelView<TValue>(props: CascadeLevelViewProps<TValue>) {
  const { level, value, disabled, className, onChange } = props;

  let control: ReactNode;

  if (level.control === CascadeControl.Pills) {
    control = (
      <PillButtonGroup
        mode={PillSelectionMode.Toggle}
        options={level.options}
        color={level.color}
        disabled={disabled}
        value={getSingleValue(value, level.key)}
        onChange={(newValue) => {
          onChange(level, newValue);
        }}
      />
    );
  } else if (level.multiple) {
    const selected = getMultipleValues(value, level.key);

    control = (
      <MultiSelectDropDownInput
        options={level.options}
        getOptionLabel={'title'}
        classes={dropdownClasses}
        endAdornmentClassname={styles.endAdornment}
        isOptionEqualToValue={(option, other) => option.value === other.value}
        value={level.options.filter((option) =>
          selected.includes(option.value),
        )}
        placeholder={level.placeholder}
        hasSearchFilter={level.enableSearch}
        allSelectedLabel={level.allSelectedLabel}
        hideHeader={true}
        showResetFooter={true}
        disabled={disabled}
        onValueChanged={(options) => {
          onChange(
            level,
            options.map((option) => option.value),
          );
        }}
      />
    );
  } else {
    const selected = getSingleValue(value, level.key);

    control = (
      <DropDownInput
        options={level.options}
        classes={dropdownClasses}
        endAdornmentClassname={styles.endAdornment}
        getOptionLabel={'title'}
        isOptionEqualToValue={(option, other) => option?.value === other?.value}
        value={
          level.options.find((option) => option.value === selected) ?? null
        }
        placeholder={level.placeholder}
        enableSearch={level.enableSearch}
        required={level.required}
        disabled={disabled}
        onValueChanged={(option: CascadeOption<TValue> | null) => {
          onChange(level, option?.value);
        }}
      />
    );
  }

  const levelClassName = clsx(styles.level, className, level.className);

  return level.title ? (
    <Field
      title={level.title}
      testId={level.testId}
      className={levelClassName}
      childrenWrapperClassName={styles.levelChildWrapper}
    >
      {control}
    </Field>
  ) : (
    <div data-test-id={level.testId} className={levelClassName}>
      {control}
    </div>
  );
}

const dropdownClasses = {
  option: dropdownStyles.selectorOption,
  paper: dropdownStyles.selectorPaper,
  popper: dropdownStyles.selectorPopper,
  input: styles.input,
};
